import {SubmissionChecklist} from './ResearcherTools';
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
// src/components/ApplicationForm.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactSignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import { useAuth } from 'contexts/AuthContext';
import './ApplicationForm.css';

/** Signature canvas narrow ref interface */
interface SignatureCanvasRef {
  clear: () => void;
  getCanvas: () => HTMLCanvasElement;
}

/* ============================
   Constants & Label Mappers
============================ */
const API_BASE = '/v1/api/research-applications';
const MAX_DOC_BYTES = 10 * 1024 * 1024; // 10 MiB
const MAX_TOTAL_DOC_BYTES = 50 * 1024 * 1024; // server request limit
const MAX_TEXT_CHARS = 255;
const MAX_DESCRIPTION_CHARS = 2000;

const requiredDocuments = [
  'Consent Form',
  'Research Protocol/Information Sheets',
  'Data Collection Tool',
  'Authorisation Letter From Consent Authority',
];

const applicationTypeOptions = ['FACULTY', 'INSTITUTE'] as const;
type ApplicationType = (typeof applicationTypeOptions)[number] | '';

const researchDesignKeys = [
  'quantitative',
  'qualitative',
  'mixedMethods',
  'actionResearch',
  'other',
] as const;

const researchDesignLabels: Record<(typeof researchDesignKeys)[number], string> = {
  quantitative: 'Quantitative',
  qualitative: 'Qualitative',
  mixedMethods: 'Mixed Methods',
  actionResearch: 'Action Research',
  other: 'Other',
};

const dataToolKeys = [
  'experimental',
  'semiStructuredInterviews',
  'participatoryObservations',
  'nonParticipatoryObservations',
  'structuredInterviews',
  'openEndedInterviews',
  'questionnairesSurvey',
  'other',
] as const;

const dataToolLabels: Record<(typeof dataToolKeys)[number], string> = {
  experimental: 'Experimental',
  semiStructuredInterviews: 'Semi-Structured Interviews',
  participatoryObservations: 'Participatory Observations',
  nonParticipatoryObservations: 'Non-Participatory Observations',
  structuredInterviews: 'Structured Interviews',
  openEndedInterviews: 'Open-Ended Interviews',
  questionnairesSurvey: 'Questionnaires / Survey',
  other: 'Other',
};

interface ResearchDesign {
  quantitative: boolean;
  qualitative: boolean;
  mixedMethods: boolean;
  actionResearch: boolean;
  other: boolean;
}

interface DataCollectionTools {
  experimental: boolean;
  semiStructuredInterviews: boolean;
  participatoryObservations: boolean;
  nonParticipatoryObservations: boolean;
  structuredInterviews: boolean;
  openEndedInterviews: boolean;
  questionnairesSurvey: boolean;
  other: boolean;
}

interface ApplicationData {
  // Stage One
  applicationType: ApplicationType;
  titleOfResearch: string;
  researchDescription: string;
  proposalDefended: boolean;
  relevantExperience: string;
  academicQualifications: string;
  fundingOfResearch: string;
  coResearchers: string;
  coResearchersList: { fullName: string; occupation: string }[];
  researchDesign: ResearchDesign;
  researchDesignOtherText?: string;
  dataCollectionTools: DataCollectionTools;
  dataCollectionToolsOtherText?: string;

  // Stage Two
  categoryOfParticipants: string[];
  levelOfSensitivity: string;
  researchLocation: string[];

  // Stage Three
  proposedFieldwork: string;
  fieldworkDetails: string;

  // Stage Four
  documents: { [key: string]: File | null };

  // Stage Five
  signatureData: string;
  declarationAccepted: boolean;
}

/* ============================
   Helpers
============================ */

const createEmptyFormData = (): ApplicationData => ({
  applicationType: '',
  titleOfResearch: '',
  researchDescription: '',
  proposalDefended: false,
  relevantExperience: '',
  academicQualifications: '',
  fundingOfResearch: '',
  coResearchers: '',
  coResearchersList: [],
  researchDesign: {
    quantitative: false,
    qualitative: false,
    mixedMethods: false,
    actionResearch: false,
    other: false,
  },
  researchDesignOtherText: '',
  dataCollectionTools: {
    experimental: false,
    semiStructuredInterviews: false,
    participatoryObservations: false,
    nonParticipatoryObservations: false,
    structuredInterviews: false,
    openEndedInterviews: false,
    questionnairesSurvey: false,
    other: false,
  },
  dataCollectionToolsOtherText: '',
  categoryOfParticipants: [],
  levelOfSensitivity: '',
  researchLocation: [],
  proposedFieldwork: '',
  fieldworkDetails: '',
  documents: {},
  signatureData: '',
  declarationAccepted: false,
});

const hasValue = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (typeof val === 'number') return true;
  if (typeof val === 'boolean') return true;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
};

const backendDocumentTypeToFormKey = (type: string | null | undefined): string | null => {
  switch ((type || '').toLowerCase()) {
    case 'consent_form':
      return 'Consent Form';
    case 'research_protocol':
    case 'information_sheet':
      return 'Research Protocol/Information Sheets';
    case 'data_collection_tool':
      return 'Data Collection Tool';
    case 'participant_consent_letter':
    case 'authorisation_letter_from_consent_authority':
      return 'Authorisation Letter From Consent Authority';
    default:
      return null;
  }
};

/* ============================
   Component
============================ */
const ApplicationForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useAuth();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  const userId = (account as any)?.id;

  const resumeStepFromState =
    (location.state as any)?.resumeStep &&
    Number((location.state as any).resumeStep);

  // ----- persistent state -----
  const [step, setStep] = useState<number>(() => {
    if (
      isEditMode &&
      resumeStepFromState &&
      resumeStepFromState >= 1 &&
      resumeStepFromState <= 5
    ) {
      return resumeStepFromState;
    }

    if (isEditMode) {
      const params = new URLSearchParams(window.location.search);
      const urlStep = Number(params.get('step'));
      if (urlStep >= 1 && urlStep <= 5) return urlStep;
      return 1;
    }

    const saved = localStorage.getItem('app.step');
    return saved ? Number(saved) : 1;
  });

  const [applicationId, setApplicationId] = useState<number | null>(() => {
    if (isEditMode && id) return Number(id);
    const saved = localStorage.getItem('app.applicationId');
    return saved ? Number(saved) : null;
  });

  const [formData, setFormData] = useState<ApplicationData>(() => {
    if (!isEditMode) {
      const saved = localStorage.getItem('app.formData');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (!parsed.documents) parsed.documents = {};
          if (!parsed.applicationType) parsed.applicationType = '';
          return parsed;
        } catch {
          // ignore parse error
        }
      }
    }
    return createEmptyFormData();
  });

  // errors + UI
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [textCutMsg, setTextCutMsg] = useState<Record<string, string>>({});
  const [docUploading, setDocUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [existingDocumentTypes, setExistingDocumentTypes] = useState<string[]>([]);
  const [revisionRemarks, setRevisionRemarks] = useState<string>('');

  // popups
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [institutionInput, setInstitutionInput] = useState('');
  const [principalInvestigatorInput, setPrincipalInvestigatorInput] = useState('');
  const [purposeOfResearchInput, setPurposeOfResearchInput] = useState('');
  const [participantActivitiesInput, setParticipantActivitiesInput] = useState('');

  const [showCoResearcherPopup, setShowCoResearcherPopup] = useState(false);
  const [newCoResearcher, setNewCoResearcher] = useState({ fullName: '', occupation: '' });

  const [submissionPopupVisible, setSubmissionPopupVisible] = useState(false);

  const signatureRef = useRef<SignatureCanvasRef | null>(null);

  const [animKey, setAnimKey] = useState(0);

  const declRef = useRef<HTMLDivElement | null>(null);
  const [declFullyScrolled, setDeclFullyScrolled] = useState(false);
  const isReturnedEdit = isEditMode && (applicationStatus || '').toUpperCase() === 'NOT_RECOMMENDED';

  const participantOptions = [
    'Minors (Under 18)',
    'Unaccompanied Minors',
    'Mentally/Physically Limited',
    'Adults (Over 18)',
    'Orphaned',
    'Illiterate',
    'Other',
  ];
  const locationOptions = [
    'Pre-school',
    'Primary School',
    'Secondary School',
    'Tertiary Institution',
    'Private Organisation',
    'Family',
    'Health Institution',
    'Community',
    'Individual',
    'Other',
  ];

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  /* ============================
     Prefill for EDIT / RESUME
  ============================ */
  useEffect(() => {
    if (!isEditMode || !applicationId) return;

    const stateApp = (location.state as any)?.application;

    const hydrateFromDetails = (appDetails: any) => {
      if (!appDetails) return;
      setApplicationStatus(appDetails.status || null);
      setRevisionRemarks(appDetails.revisionRemarks || '');
      const activeDocTypes = (Array.isArray(appDetails.documents) ? appDetails.documents : [])
        .filter((doc: any) => doc && doc.active !== false)
        .map((doc: any) => backendDocumentTypeToFormKey(doc.documentType))
        .filter(Boolean) as string[];
      setExistingDocumentTypes(Array.from(new Set(activeDocTypes)));

      setFormData((prev) => {
        let design: ResearchDesign = prev.researchDesign;
        try {
          const raw =
            typeof appDetails.researchDesign === 'string'
              ? JSON.parse(appDetails.researchDesign)
              : appDetails.researchDesign || {};
          design = {
            quantitative: !!raw.quantitative,
            qualitative: !!raw.qualitative,
            mixedMethods: !!raw.mixedMethods,
            actionResearch: !!raw.actionResearch,
            other: !!raw.other,
          };
        } catch {
          // ignore
        }

        let tools: DataCollectionTools = prev.dataCollectionTools;
        try {
          const raw =
            typeof appDetails.researchTool === 'string'
              ? JSON.parse(appDetails.researchTool)
              : appDetails.researchTool || {};
          tools = {
            experimental: !!raw.experimental,
            semiStructuredInterviews: !!raw.semiStructuredInterviews,
            participatoryObservations: !!raw.participatoryObservations,
            nonParticipatoryObservations: !!raw.nonParticipatoryObservations,
            structuredInterviews: !!raw.structuredInterviews,
            openEndedInterviews: !!raw.openEndedInterviews,
            questionnairesSurvey: !!raw.questionnairesSurvey,
            other: !!raw.other,
          };
        } catch {
          // ignore
        }

        let coList: { fullName: string; occupation: string }[] = prev.coResearchersList;
        if (hasValue(appDetails.coResearchersDetails)) {
          coList = String(appDetails.coResearchersDetails)
            .split(';')
            .map((chunk: string) => chunk.trim())
            .filter(Boolean)
            .map((chunk: string) => {
              const [fullName, occupation] = chunk.split('-').map((x) => x.trim());
              return { fullName: fullName || chunk, occupation: occupation || '' };
            });
        }

        const categories = (() => {
          const v = appDetails.categoryOfParticipants;
          if (!v) return prev.categoryOfParticipants;
          if (Array.isArray(v)) return v;
          return String(v)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        })();

        const locations = (() => {
          const v = appDetails.researchLocation;
          if (!v) return prev.researchLocation;
          if (Array.isArray(v)) return v;
          return String(v)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        })();

        return {
          ...prev,
          applicationType:
            appDetails.applicationType === 'FACULTY' || appDetails.applicationType === 'INSTITUTE'
              ? appDetails.applicationType
              : prev.applicationType,
          titleOfResearch: appDetails.researchTitle || prev.titleOfResearch,
          researchDescription: appDetails.researchDescription || prev.researchDescription,
          proposalDefended:
            appDetails.proposalDefended !== undefined
              ? !!appDetails.proposalDefended
              : prev.proposalDefended,
          relevantExperience: appDetails.relevantExperience || prev.relevantExperience,
          academicQualifications: appDetails.academicQualifications || prev.academicQualifications,
          fundingOfResearch: appDetails.fundingOfResearch || prev.fundingOfResearch,
          coResearchersList: coList,
          researchDesign: design,
          researchDesignOtherText:
            appDetails.researchDesignOtherText || prev.researchDesignOtherText || '',
          dataCollectionTools: tools,
          dataCollectionToolsOtherText:
            appDetails.dataCollectionToolsOtherText || prev.dataCollectionToolsOtherText || '',
          categoryOfParticipants: categories,
          levelOfSensitivity: appDetails.levelOfSensitivity || prev.levelOfSensitivity,
          researchLocation: locations,
          proposedFieldwork: appDetails.proposedFieldwork || prev.proposedFieldwork,
          fieldworkDetails: appDetails.fieldworkDetails || prev.fieldworkDetails,
          documents: prev.documents || {},
          signatureData: '',
          declarationAccepted: false,
        };
      });
    };

    if (stateApp) {
      hydrateFromDetails(stateApp);
      return;
    }

    (async () => {
      try {
        const query = userId !== undefined && userId !== null ? `?userId=${encodeURIComponent(String(userId))}` : '';
        const resp = await fetch(
          `/v1/api/research-applications/details/${applicationId}${query}`
        );
        const data = await resp.json();
        if (!data.success) {
          console.error('Failed to fetch application details', data.message);
          return;
        }
        hydrateFromDetails(data.data);
      } catch (e) {
        console.error('Error fetching application details for edit', e);
      }
    })();
  }, [isEditMode, applicationId, location.state, userId]);

  /* ============================
     Autosave draft (NEW ONLY)
  ============================ */
  useEffect(() => {
    if (isEditMode) return;
    const t = setTimeout(() => {
      localStorage.setItem('app.formData', JSON.stringify(formData));
      localStorage.setItem('app.step', String(step));
      if (applicationId) localStorage.setItem('app.applicationId', String(applicationId));
    }, 350);
    return () => clearTimeout(t);
  }, [formData, step, applicationId, isEditMode]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (step === 5) {
      setDeclFullyScrolled(false);
      setFormData((prev) => ({ ...prev, declarationAccepted: false }));
      setTimeout(() => {
        if (!declRef.current) return;
        declRef.current.scrollTop = 0;
      }, 0);
    }
  }, [step]);

  /* ============================
     Handlers
  ============================ */
  const remainingChars = (val: string, limit = MAX_TEXT_CHARS) => Math.max(0, limit - (val?.length || 0));

  const setLimitedText = (field: keyof ApplicationData, incoming: string, limit = MAX_TEXT_CHARS) => {
    const trimmed = incoming.slice(0, limit);

    setTextCutMsg((prev) => ({
      ...prev,
      [field]: incoming.length > limit
        ? `The text was cut to the maximum (${limit} characters).`
        : '',
    }));

    setFormData((prev) => ({
      ...prev,
      [field]: trimmed,
    }));
  };

  const handleLimitedTextareaChange = (
    field: keyof ApplicationData,
    e: React.ChangeEvent<HTMLTextAreaElement>,
    limit = MAX_TEXT_CHARS
  ) => {
    setLimitedText(field, e.target.value, limit);
  };

  const handleLimitedTextareaPaste = (
    field: keyof ApplicationData,
    e: React.ClipboardEvent<HTMLTextAreaElement>,
    limit = MAX_TEXT_CHARS
  ) => {
    e.preventDefault();

    const paste = e.clipboardData.getData('text') || '';
    const current = String(formData[field] || '');
    const start = e.currentTarget.selectionStart ?? current.length;
    const end = e.currentTarget.selectionEnd ?? current.length;

    const next = current.slice(0, start) + paste + current.slice(end);
    const truncated = next.slice(0, limit);

    setTextCutMsg((prev) => ({
      ...prev,
      [field]: next.length > limit
        ? `The text was cut to the maximum (${limit} characters).`
        : '',
    }));

    setFormData((prev) => ({
      ...prev,
      [field]: truncated,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    parentKey: 'researchDesign' | 'dataCollectionTools'
  ) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [parentKey]: { ...prev[parentKey], [name]: checked },
      ...(name === 'other' && !checked
        ? parentKey === 'researchDesign'
          ? { researchDesignOtherText: '' }
          : { dataCollectionToolsOtherText: '' }
        : {}),
    }));
  };

  const handleCategoryOptionChange = (option: string) => {
    setFormData((prev) => {
      const exists = prev.categoryOfParticipants.includes(option);
      const list = exists
        ? prev.categoryOfParticipants.filter((o) => o !== option)
        : [...prev.categoryOfParticipants, option];
      return { ...prev, categoryOfParticipants: list };
    });
    setCategoryOpen(false);
  };

  const handleLocationOptionChange = (loc: string) => {
    setFormData((prev) => {
      const exists = prev.researchLocation.includes(loc);
      const list = exists ? prev.researchLocation.filter((o) => o !== loc) : [...prev.researchLocation, loc];
      return { ...prev, researchLocation: list };
    });
    setLocationOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, docKey: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_DOC_BYTES) {
      setErrors((prev) => ({
        ...prev,
        [docKey]: 'File too large. Max 10 MiB per document.',
      }));

      e.target.value = '';

      setFormData((prev) => ({
        ...prev,
        documents: { ...prev.documents, [docKey]: null },
      }));
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      if (next[docKey] === 'File too large. Max 10 MiB per document.') delete next[docKey];
      return next;
    });

    setFormData((prev) => ({
      ...prev,
      documents: { ...prev.documents, [docKey]: file },
    }));
  };

  const openConsentPopup = () => {
    setInstitutionInput('');
    setPrincipalInvestigatorInput('');
    setPurposeOfResearchInput('');
    setParticipantActivitiesInput('');
    setShowConsentPopup(true);
  };

  const handleConsentGenerate = async () => {
    if (!institutionInput.trim()) {
      alert('Institution is required to generate consent form.');
      return;
    }

    try {
      const pi = principalInvestigatorInput || 'Principal Investigator Name';
      const doc = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;

      const logo = new Image();
      logo.src = '/msu.png';
      await new Promise<void>((res) => {
        logo.onload = () => res();
        logo.onerror = () => res();
      });
      const imgW = 40;
      const imgH = 40;
      const imgX = (pageWidth - imgW) / 2;
      let y = margin;
      try {
        doc.addImage(logo, 'PNG', imgX, y, imgW, imgH);
        y += imgH + 8;
      } catch {
        y += 8;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Research Participant Consent Form', pageWidth / 2, y, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      y += 12;

      const left = margin;
      doc.text(`Title of Research Study: ${formData.titleOfResearch}`, left, y);
      y += 8;
      doc.text(`Principal Investigator(s): ${pi}`, left, y);
      y += 12;

      doc.setFont('helvetica', 'bold');
      doc.text("Participant's General Information", left, y);
      doc.setFont('helvetica', 'normal');
      y += 8;
      doc.text(
        'Sex: ____    Age: ____    Employment Status: YES/NO    Education Level: ______________',
        left,
        y
      );
      y += 12;

      const paras = [
        `You are invited to participate in a research study conducted by ${pi} from ${institutionInput}. The purpose of this study is to ${purposeOfResearchInput}. Your participation is voluntary. If you agree, you will be asked to ${participantActivitiesInput}.`,
        `There may be no direct benefit or compensation. However, your participation may help generate knowledge that benefits others. Information will remain confidential, identities will not be disclosed, and data will be stored securely.`,
      ];
      paras.forEach((p) => {
        const lines = doc.splitTextToSize(p, pageWidth - margin * 2);
        if (y + lines.length * 6 > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(lines, left, y);
        y += lines.length * 6 + 8;
      });

      doc.setFont('helvetica', 'bold');
      doc.text('By signing below, you confirm that:', left, y);
      doc.setFont('helvetica', 'normal');
      y += 8;
      const bullets = [
        'You have read and understood this consent form.',
        'You voluntarily agree to participate in this study.',
        'You may withdraw at any time without penalty.',
      ];
      bullets.forEach((pt) => {
        const wrapped = doc.splitTextToSize(`• ${pt}`, pageWidth - margin * 2);
        if (y + wrapped.length * 6 > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(wrapped, left + 5, y);
        y += wrapped.length * 6 + 4;
      });

      const sigLines = [
        '',
        'Participant: ________________   Signature _______________   Date: _______________',
        '',
        'Researcher: ________________   Signature _______________   Date: ________________',
      ];
      sigLines.forEach((line) => {
        if (y + 10 > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, left, y);
        y += 12;
      });

      doc.save('ConsentForm.pdf');
      setShowConsentPopup(false);
    } catch (err) {
      console.error(err);
      alert('Failed to generate Consent Form. Please try again.');
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setFormData((prev) => ({ ...prev, signatureData: '' }));
  };

  const handleEndSignature = () => {
    if (signatureRef.current) {
      const dataUrl = signatureRef.current.getCanvas().toDataURL('image/png');
      setFormData((prev) => ({ ...prev, signatureData: dataUrl }));
    }
  };

  const handleAddCoResearcher = () => {
    if (!newCoResearcher.fullName.trim() || !newCoResearcher.occupation.trim()) {
      alert('Please fill in both Full Name and Occupation.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      coResearchersList: [...prev.coResearchersList, { ...newCoResearcher }],
    }));
    setNewCoResearcher({ fullName: '', occupation: '' });
    setShowCoResearcherPopup(false);
  };

  const removeCoResearcher = (i: number) => {
    setFormData((prev) => ({
      ...prev,
      coResearchersList: prev.coResearchersList.filter((_, idx) => idx !== i),
    }));
  };

  const onDeclScroll = () => {
    const el = declRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 6;
    if (atBottom) setDeclFullyScrolled(true);
  };

  /* ============================
     Validation
  ============================ */
  const validateStep = (): boolean => {
    const newErr: { [key: string]: string } = {};

    if (step === 1) {
      if (!formData.applicationType) {
        newErr.applicationType = 'Please select Faculty or Institute';
      }
      if (!formData.titleOfResearch.trim()) {
        newErr.titleOfResearch = 'Title of Research is required';
      }
    }

    if (step === 4) {
      requiredDocuments.forEach((doc) => {
        if (!formData.documents[doc] && !existingDocumentTypes.includes(doc)) {
          newErr[doc] = 'Required';
        }
      });
    }

    if (step === 5) {
      if (!formData.declarationAccepted) newErr.declarationAccepted = 'You must accept the declaration';
      if (!formData.signatureData) newErr.signatureData = 'Signature is required';
    }

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  /* ============================
     Next / Previous / Submit
  ============================ */
  const goToStep = (n: number) => {
    setStep(n);
    setAnimKey((k) => k + 1);
  };

  const handleNext = async () => {
    if (isAdvancing || !validateStep()) return;
    setIsAdvancing(true);

    try {
      if (step === 1) {
        const coDetails = formData.coResearchersList
          .map((c) => `${c.fullName} - ${c.occupation}`)
          .join('; ');

        const payload1 = {
          userId,
          applicationType: formData.applicationType,
          researchTitle: formData.titleOfResearch,
          researchDescription: formData.researchDescription,
          proposalDefended: formData.proposalDefended,
          relevantExperience: formData.relevantExperience,
          coResearchersDetails: coDetails,
          academicQualifications: formData.academicQualifications,
          fundingOfResearch: formData.fundingOfResearch,
          researchDesign: JSON.stringify(formData.researchDesign),
          researchDesignOtherText: formData.researchDesignOtherText || '',
          researchTool: JSON.stringify(formData.dataCollectionTools),
          dataCollectionToolsOtherText: formData.dataCollectionToolsOtherText || '',
        };

        if (applicationId) {
          const resp = await fetch(`${API_BASE}/${applicationId}/stage-one`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload1),
          });
          const data = await resp.json();
          if (!data.success) return alert(data.message || 'Failed to update step 1.');
        } else {
          const resp = await fetch(`${API_BASE}/stage-one`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload1),
          });
          const data = await resp.json();
          if (!data.success) return alert(data.message || 'Failed to create application.');
          setApplicationId(data.data.id);
          localStorage.setItem('app.applicationId', String(data.data.id));
          navigate(`/applications/edit/${data.data.id}?step=2`, { replace: true, state: { resumeStep: 2 } });
        }

        goToStep(2);
        return;
      }

      if (step === 2) {
        if (!applicationId) return alert('Application ID missing.');
        const payload2 = {
          userId,
          categoryOfParticipants: formData.categoryOfParticipants.join(', '),
          levelOfSensitivity: formData.levelOfSensitivity,
          researchLocation: formData.researchLocation.join(', '),
        };
        const resp2 = await fetch(`${API_BASE}/${applicationId}/stage-two`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload2),
        });
        const data2 = await resp2.json();
        if (!data2.success) return alert(data2.message || 'Failed to save step 2.');
        goToStep(3);
        return;
      }

      if (step === 3) {
        if (!applicationId) return alert('Application ID missing.');
        const payload3 = {
          userId,
          proposedFieldwork: formData.proposedFieldwork,
          fieldworkDetails: formData.fieldworkDetails,
        };
        const resp3 = await fetch(`${API_BASE}/${applicationId}/stage-three`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload3),
        });
        const data3 = await resp3.json();
        if (!data3.success) return alert(data3.message || 'Failed to save step 3.');
        goToStep(4);
        return;
      }

      if (step === 4) {
        if (!applicationId) return alert('Application ID missing.');
        setDocUploading(true);

        const fieldMap: Record<string, string> = {
          'Consent Form': 'consentForm',
          'Research Protocol/Information Sheets': 'researchProtocolInformationSheets',
          'Data Collection Tool': 'dataCollectionTool',
          'Authorisation Letter From Consent Authority': 'authorisationLetterFromConsentAuthority',
        };

        const totalBytes = requiredDocuments.reduce((sum, docKey) => {
          const f = formData.documents[docKey];
          return sum + (f ? f.size : 0);
        }, 0);

        if (totalBytes > MAX_TOTAL_DOC_BYTES) {
          setDocUploading(false);
          alert(
            `Total upload is too large (${(totalBytes / 1024 / 1024).toFixed(1)} MiB). ` +
            `Please keep total under ${(MAX_TOTAL_DOC_BYTES / 1024 / 1024)} MiB.`
          );
          return;
        }

        const form = new FormData();
        requiredDocuments.forEach((docKey) => {
          const file = formData.documents[docKey];
          if (file) form.append(fieldMap[docKey], file);
        });

        const query = userId !== undefined && userId !== null ? `?userId=${encodeURIComponent(String(userId))}` : '';
        const resp4 = await fetch(`${API_BASE}/${applicationId}/stage-four${query}`, {
          method: 'PATCH',
          body: form,
        });

        const ct = (resp4.headers.get('content-type') || '').toLowerCase();
        const rawText = await resp4.text();

        if (!ct.includes('application/json')) {
          setDocUploading(false);

          if (resp4.status === 413) {
            alert(
              'Upload failed (413): total upload size is too large. ' +
              'Upload smaller files or fewer files at once.'
            );
          } else {
            alert(`Upload failed (HTTP ${resp4.status}). Server returned a non-JSON response.`);
          }
          return;
        }

        let data4: any;
        try {
          data4 = JSON.parse(rawText);
        } catch {
          setDocUploading(false);
          alert('Upload failed: invalid JSON response from server.');
          return;
        }

        setDocUploading(false);

        if (!resp4.ok || !data4.success) {
          if (resp4.status === 413) {
            alert(
              'Upload failed (413): total upload size is too large. ' +
              'Upload smaller files or fewer files at once.'
            );
          } else {
            alert(data4.message || 'Failed to upload documents.');
          }
          return;
        }

        goToStep(5);
        return;
      }
    } catch (error) {
      console.error(error);
      alert('An error occurred. Please try again.');
      setDocUploading(false);
    } finally {
      setIsAdvancing(false);
    }
  };

  const handlePrevious = () => {
    goToStep(Math.max(1, step - 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep() || submitting) return;
    if (!applicationId) return alert('Application ID missing.');
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/${applicationId}/${isReturnedEdit ? 'resubmit' : 'stage-five'}`, {
        method: isReturnedEdit ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ declarationAccepted: true, userId }),
      });
      const data = await resp.json();
      if (!data.success) {
        setSubmitting(false);
        return alert(data.message || (isReturnedEdit ? 'Resubmission failed.' : 'Submission failed.'));
      }
      localStorage.removeItem('app.formData');
      localStorage.removeItem('app.step');
      localStorage.removeItem('app.applicationId');
      setSubmissionPopupVisible(true);
    } catch (err) {
      console.error(err);
      alert(isReturnedEdit ? 'An error occurred during resubmission.' : 'An error occurred during submission.');
      setSubmitting(false);
    }
  };

  /* ============================
     Render helpers
  ============================ */
  const stepLabels = useMemo(
    () => ['Research Details', 'Participants & Context', 'Fieldwork', 'Documents', 'Declaration'],
    []
  );

  const canGoNext = !isAdvancing && !docUploading && !submitting;

  const progressPct = ((step - 1) / (5 - 1)) * 100;

  const buildStageOnePayload = () => {
    const coDetails = formData.coResearchersList
      .map((c) => `${c.fullName} - ${c.occupation}`)
      .join('; ');

    return {
      userId,
      applicationType: formData.applicationType || null,
      researchTitle: formData.titleOfResearch,
      researchDescription: formData.researchDescription,
      proposalDefended: formData.proposalDefended,
      relevantExperience: formData.relevantExperience,
      coResearchersDetails: coDetails,
      academicQualifications: formData.academicQualifications,
      fundingOfResearch: formData.fundingOfResearch,
      researchDesign: JSON.stringify(formData.researchDesign),
      researchDesignOtherText: formData.researchDesignOtherText || '',
      researchTool: JSON.stringify(formData.dataCollectionTools),
      dataCollectionToolsOtherText: formData.dataCollectionToolsOtherText || '',
    };
  };

  const persistCurrentStepAsDraft = async () => {
    try {
      setIsAdvancing(true);
      if (step === 1 || !applicationId) {
        const payload1 = buildStageOnePayload();
        if (applicationId) {
          const resp = await fetch(`${API_BASE}/${applicationId}/stage-one`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload1),
          });
          const data = await resp.json();
          if (!data.success) throw new Error(data.message || 'Failed to save draft.');
        } else {
          const resp = await fetch(`${API_BASE}/stage-one`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload1),
          });
          const data = await resp.json();
          if (!data.success) throw new Error(data.message || 'Failed to create draft.');
          setApplicationId(data.data.id);
          localStorage.setItem('app.applicationId', String(data.data.id));
          navigate(`/applications/edit/${data.data.id}?step=${step}`, { replace: true, state: { resumeStep: step } });
        }
        alert('Draft saved.');
        return;
      }

      if (step === 2) {
        const payload2 = {
          userId,
          categoryOfParticipants: formData.categoryOfParticipants.join(', '),
          levelOfSensitivity: formData.levelOfSensitivity,
          researchLocation: formData.researchLocation.join(', '),
        };
        const resp = await fetch(`${API_BASE}/${applicationId}/stage-two`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload2),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.message || 'Failed to save draft.');
      } else if (step === 3) {
        const payload3 = {
          userId,
          proposedFieldwork: formData.proposedFieldwork,
          fieldworkDetails: formData.fieldworkDetails,
        };
        const resp = await fetch(`${API_BASE}/${applicationId}/stage-three`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload3),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.message || 'Failed to save draft.');
      } else if (step === 4) {
        const fieldMap: Record<string, string> = {
          'Consent Form': 'consentForm',
          'Research Protocol/Information Sheets': 'researchProtocolInformationSheets',
          'Data Collection Tool': 'dataCollectionTool',
          'Authorisation Letter From Consent Authority': 'authorisationLetterFromConsentAuthority',
        };
        const form = new FormData();
        let count = 0;
        requiredDocuments.forEach((docKey) => {
          const file = formData.documents[docKey];
          if (file) {
            form.append(fieldMap[docKey], file);
            count += 1;
          }
        });
        if (count > 0) {
          const query = userId !== undefined && userId !== null ? `?userId=${encodeURIComponent(String(userId))}` : '';
          const resp = await fetch(`${API_BASE}/${applicationId}/stage-four${query}`, {
            method: 'PATCH',
            body: form,
          });
          const data = await resp.json();
          if (!resp.ok || !data.success) throw new Error(data.message || 'Failed to save draft documents.');
        }
      }
      alert('Draft saved.');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to save draft.');
    } finally {
      setIsAdvancing(false);
    }
  };

  return (
    <div className="app-application-form">
      <h1>{isReturnedEdit ? 'Revise Application' : isEditMode ? 'Resume Application' : 'New Application'}</h1>
      <hr className="section-separator" />

      {isReturnedEdit && (
        <div className="hint-text" style={{ marginBottom: '1rem' }}>
          Revisions are required before this application can continue review.
          {revisionRemarks ? <div><strong>Revision remarks:</strong> {revisionRemarks}</div> : null}
        </div>
      )}

      <div className="progress-wrap">
        <p className="ops-step-summary" aria-live="polite">Step {step} of {stepLabels.length} · {stepLabels[step - 1]}</p>
        <div className="progress-indicator">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className={`progress-step ${step > n ? 'completed' : ''} ${step === n ? 'current' : ''}`}
            >
              <div className="step-number">{n}</div>
              <div className="step-label">{stepLabels[n - 1]}</div>
            </div>
          ))}
        </div>
        <div className="progress-line" aria-hidden="true">
          <div
            className="progress-line-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <form onSubmit={step === 5 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
        <div key={animKey} className="step-viewport animate-step">
          {step === 1 && (
            <>
              <h2>Step 1: Qualifications & Research Details</h2>
              <hr className="step-separator" />

              <div className="app-form-group">
                <label>Application Route</label>
                <select aria-label="application Type"
                  name="applicationType"
                  value={formData.applicationType}
                  onChange={handleChange}
                  className={errors.applicationType ? 'error' : ''}
                >
                  <option value="">Select route</option>
                  <option value="FACULTY">Faculty application</option>
                  <option value="INSTITUTE">Institute application</option>
                </select>
                {errors.applicationType && (
                  <p className="app-error-message">{errors.applicationType}</p>
                )}
              </div>

              <div className="app-form-group">
                <label>Title of Research</label>
                <input aria-label="Enter a clear, concise study title"
                  type="text"
                  name="titleOfResearch"
                  value={formData.titleOfResearch}
                  onChange={handleChange}
                  className={errors.titleOfResearch ? 'error' : ''}
                  placeholder="Enter a clear, concise study title"
                />
                {errors.titleOfResearch && <p className="app-error-message">{errors.titleOfResearch}</p>}
              </div>

              <div className="app-form-group">
                <label>Research Description</label>
                <textarea aria-label="Briefly describe the purpose, scope, and expected outcomes..."
                  name="researchDescription"
                  value={formData.researchDescription}
                  maxLength={MAX_DESCRIPTION_CHARS}
                  onChange={(e) => handleLimitedTextareaChange('researchDescription', e, MAX_DESCRIPTION_CHARS)}
                  onPaste={(e) => handleLimitedTextareaPaste('researchDescription', e, MAX_DESCRIPTION_CHARS)}
                  aria-describedby="research-description-remaining"
                  placeholder="Briefly describe the purpose, scope, and expected outcomes..."
                />
                <div className="char-meta">
                  <span id="research-description-remaining" className="char-remaining">
                    {remainingChars(formData.researchDescription, MAX_DESCRIPTION_CHARS)} characters remaining
                  </span>
                  {!!textCutMsg.researchDescription && (
                    <span className="char-cut-msg">{textCutMsg.researchDescription}</span>
                  )}
                </div>
              </div>

              <div className="app-form-group">
                <label>Proposal Defended (if applicable)</label>
                <div className="radio-row">
                  <label>
                    <input aria-label="proposal Defended"
                      type="radio"
                      name="proposalDefended"
                      value="true"
                      checked={formData.proposalDefended === true}
                      onChange={() => setFormData((p) => ({ ...p, proposalDefended: true }))}
                    />{' '}
                    Yes
                  </label>
                  <label>
                    <input aria-label="proposal Defended"
                      type="radio"
                      name="proposalDefended"
                      value="false"
                      checked={formData.proposalDefended === false}
                      onChange={() => setFormData((p) => ({ ...p, proposalDefended: false }))}
                    />{' '}
                    No
                  </label>
                </div>
              </div>

              <div className="two-col">
                <div className="app-form-group">
                  <label>Relevant Experience</label>
                  <textarea aria-label="Summarize experience relevant to this study..."
                    name="relevantExperience"
                    value={formData.relevantExperience}
                    maxLength={MAX_TEXT_CHARS}
                    onChange={(e) => handleLimitedTextareaChange('relevantExperience', e)}
                    onPaste={(e) => handleLimitedTextareaPaste('relevantExperience', e)}
                    placeholder="Summarize experience relevant to this study..."
                  />

                  <div className="char-meta">
                    <span className="char-remaining">
                      {remainingChars(formData.relevantExperience)} characters remaining
                    </span>
                    {!!textCutMsg.relevantExperience && (
                      <span className="char-cut-msg">{textCutMsg.relevantExperience}</span>
                    )}
                  </div>
                </div>

                <div className="app-form-group">
                  <label>Academic Qualifications</label>
                  <textarea aria-label="Degrees, certifications, training..."
                    name="academicQualifications"
                    value={formData.academicQualifications}
                    maxLength={MAX_TEXT_CHARS}
                    onChange={(e) => handleLimitedTextareaChange('academicQualifications', e)}
                    onPaste={(e) => handleLimitedTextareaPaste('academicQualifications', e)}
                    placeholder="Degrees, certifications, training..."
                  />

                  <div className="char-meta">
                    <span className="char-remaining">
                      {remainingChars(formData.academicQualifications)} characters remaining
                    </span>
                    {!!textCutMsg.academicQualifications && (
                      <span className="char-cut-msg">{textCutMsg.academicQualifications}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="two-col">
                <div className="app-form-group">
                  <label>Funding of Research</label>
                  <input aria-label="Self-funded / Grant (specify) / Sponsorship..."
                    type="text"
                    name="fundingOfResearch"
                    value={formData.fundingOfResearch}
                    onChange={handleChange}
                    placeholder="Self-funded / Grant (specify) / Sponsorship..."
                  />
                </div>

                <div className="app-form-group">
                  <label>Co-Researchers (optional)</label>
                  <button
                    type="button"
                    onClick={() => setShowCoResearcherPopup(true)}
                    className="generate-btn"
                  >
                    Add Co-Researcher
                  </button>
                  <div className="chips-container">
                    {formData.coResearchersList.map((cr, i) => (
                      <div key={i} className="chip">
                        <span>{cr.fullName} — {cr.occupation}</span>
                        <button type="button" onClick={() => removeCoResearcher(i)} className="chip-x">×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <hr className="step-separator" />

              <div className="app-form-group">
                <h3>Research Design</h3>
                <div className="circle-row">
                  {researchDesignKeys.map((key) => (
                    <label key={key} className="circle-custom">
                      <input
                        type="checkbox"
                        name={key}
                        checked={formData.researchDesign[key]}
                        onChange={(e) => handleCheckboxChange(e, 'researchDesign')}
                      />
                      <span className="checkmark" />
                      {researchDesignLabels[key]}
                    </label>
                  ))}
                </div>

                <div
                  className={`animated-specify-wrapper${formData.researchDesign.other ? ' slide-down' : ''}`}
                  style={{
                    maxHeight: formData.researchDesign.other ? 80 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.4s cubic-bezier(0.25,1,0.5,1)',
                  }}
                >
                  {formData.researchDesign.other && (
                    <div className="specify-other-field">
                      <label>Please specify</label>
                      <input aria-label="Specify other research design"
                        type="text"
                        name="researchDesignOtherText"
                        value={formData.researchDesignOtherText || ''}
                        onChange={handleChange}
                        placeholder="Specify other research design"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="app-form-group">
                <h3>Data Collection Tools</h3>
                <div className="circle-row">
                  {dataToolKeys.map((tool) => (
                    <label key={tool} className="circle-custom">
                      <input
                        type="checkbox"
                        name={tool}
                        checked={formData.dataCollectionTools[tool]}
                        onChange={(e) => handleCheckboxChange(e, 'dataCollectionTools')}
                      />
                      <span className="checkmark" />
                      {dataToolLabels[tool]}
                    </label>
                  ))}
                </div>

                <div
                  className={`animated-specify-wrapper${formData.dataCollectionTools.other ? ' slide-down' : ''}`}
                  style={{
                    maxHeight: formData.dataCollectionTools.other ? 80 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.4s cubic-bezier(0.25,1,0.5,1)',
                  }}
                >
                  {formData.dataCollectionTools.other && (
                    <div className="specify-other-field">
                      <label>Please specify</label>
                      <input aria-label="Specify other data collection tool"
                        type="text"
                        name="dataCollectionToolsOtherText"
                        value={formData.dataCollectionToolsOtherText || ''}
                        onChange={handleChange}
                        placeholder="Specify other data collection tool"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2>Step 2: Participants & Context</h2>
              <hr className="step-separator" />

              <div className="app-form-group">
                <label>Category of Participants</label>
                <div className="custom-multiselect">
                  <button
                    type="button"
                    className="dropdown-header"
                    onClick={() => setCategoryOpen((o) => !o)}
                  >
                    Select Category <span className="dropdown-arrow">▾</span>
                  </button>
                  {categoryOpen && (
                    <div className="dropdown-options">
                      {participantOptions.map((opt) => {
                        const selected = formData.categoryOfParticipants.includes(opt);
                        return (
                          <div
                            key={opt}
                            className={`dropdown-option ${selected ? 'selected' : ''}`}
                            onClick={() => handleCategoryOptionChange(opt)}
                          >
                            {opt}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="chips-container">
                  {formData.categoryOfParticipants.map((item) => (
                    <span key={item} className="chip">{item}</span>
                  ))}
                </div>
              </div>

              <div className="two-col">
                <div className="app-form-group">
                  <label>Level of Sensitivity</label>
                  <select aria-label="level Of Sensitivity"
                    name="levelOfSensitivity"
                    value={formData.levelOfSensitivity}
                    onChange={handleChange}
                  >
                    <option value="">Select Sensitivity</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>

                <div className="app-form-group">
                  <label>Research Location</label>
                  <div className="custom-multiselect">
                    <button
                      type="button"
                      className="dropdown-header"
                      onClick={() => setLocationOpen((o) => !o)}
                    >
                      Select Research Location <span className="dropdown-arrow">▾</span>
                    </button>
                    {locationOpen && (
                      <div className="dropdown-options">
                        {locationOptions.map((loc) => {
                          const selected = formData.researchLocation.includes(loc);
                          return (
                            <div
                              key={loc}
                              className={`dropdown-option ${selected ? 'selected' : ''}`}
                              onClick={() => handleLocationOptionChange(loc)}
                            >
                              {loc}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="chips-container">
                    {formData.researchLocation.map((loc) => (
                      <span key={loc} className="chip">{loc}</span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2>Step 3: Fieldwork & Additional Details</h2>
              <hr className="step-separator" />

              <div className="app-form-group">
                <label>Proposed Fieldwork</label>
                <textarea aria-label="Outline your planned fieldwork activities, timelines, and locations..."
                  name="proposedFieldwork"
                  value={formData.proposedFieldwork}
                  maxLength={MAX_TEXT_CHARS}
                  onChange={(e) => handleLimitedTextareaChange('proposedFieldwork', e)}
                  onPaste={(e) => handleLimitedTextareaPaste('proposedFieldwork', e)}
                  placeholder="Outline your planned fieldwork activities, timelines, and locations..."
                />

                <div className="char-meta">
                  <span className="char-remaining">
                    {remainingChars(formData.proposedFieldwork)} characters remaining
                  </span>
                  {!!textCutMsg.proposedFieldwork && (
                    <span className="char-cut-msg">{textCutMsg.proposedFieldwork}</span>
                  )}
                </div>
              </div>

              <div className="app-form-group">
                <label>Fieldwork Details</label>
                <textarea aria-label="Provide any additional information or context about your fieldwork..."
                  name="fieldworkDetails"
                  value={formData.fieldworkDetails}
                  maxLength={MAX_TEXT_CHARS}
                  onChange={(e) => handleLimitedTextareaChange('fieldworkDetails', e)}
                  onPaste={(e) => handleLimitedTextareaPaste('fieldworkDetails', e)}
                  placeholder="Provide any additional information or context about your fieldwork..."
                />

                <div className="char-meta">
                  <span className="char-remaining">
                    {remainingChars(formData.fieldworkDetails)} characters remaining
                  </span>
                  {!!textCutMsg.fieldworkDetails && (
                    <span className="char-cut-msg">{textCutMsg.fieldworkDetails}</span>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2>Step 4: Document Upload</h2>
              <hr className="step-separator" />

              {docUploading && <p className="upload-animation">Uploading documents, please wait...</p>}
              <p>Please upload all required documents below: <strong>(Max 10 MiB per file)</strong></p>

              {requiredDocuments.map((doc) => (
                <div className="app-form-group" key={doc}>
                  <label>{doc} <span className="file-max">(Max 10 MiB)</span></label>
                  <div className="app-file-upload">
                    <input
                      type="file"
                      className={`app-input-file ${errors[doc] ? 'error' : ''}`}
                      onChange={(e) => handleFileChange(e, doc)}
                    />
                    {doc === 'Consent Form' && (
                      <button type="button" onClick={openConsentPopup} className="generate-btn">
                        Generate Consent Form
                      </button>
                    )}
                  </div>
                  {existingDocumentTypes.includes(doc) && !formData.documents[doc] && (
                    <p className="hint-text">Existing submitted document will be kept unless you choose a replacement.</p>
                  )}
                  {errors[doc] && <p className="app-error-message">{errors[doc]}</p>}
                </div>
              ))}
            </>
          )}

          {step === 5 && (
            <>
              <h2>Step 5: Declaration & Signature</h2>
              {applicationId && <SubmissionChecklist applicationId={Number(applicationId)}/>}
              <hr className="step-separator" />

              <div className="app-form-group">
                <label>Declaration</label>
                <div
                  className="terms-canvas"
                  ref={declRef}
                  onScroll={onDeclScroll}
                  style={{ background: '#fff' }}
                >
                  <p><strong>DECLARATION OF RESPONSIBILITY</strong></p>
                  <p>We the undersigned do solemnly declare that:</p>
                  <ol>
                    <li>We are aware of the Midlands State University Research Ethics Policy and hence subscribe and follow the principles of:
                      <ul>
                        <li>Voluntary participation in research</li>
                        <li>Informed consent</li>
                        <li>Confidentiality and anonymity</li>
                        <li>Trust and honesty</li>
                        <li>Safety in participation</li>
                      </ul>
                    </li>
                    <li>I am aware what plagiarism entails as articulated in Midlands State University Research Ethics Policy and hereby undertake to acknowledge all sources of information.</li>
                    <li>I am aware that any data collected belong to the University and hereby undertake to transfer all raw data to the relevant department.</li>
                    <li>I understand any amendment to the approved protocol needs REC review; non-compliance nullifies approval.</li>
                  </ol>
                </div>
                {!declFullyScrolled && (
                  <p className="hint-text">Scroll through the declaration to the bottom to enable acceptance.</p>
                )}
              </div>

              <div className="app-form-group">
                <div className={`circle-row ${!declFullyScrolled ? 'disabled' : ''}`}>
                  <label className="circle-custom" title={!declFullyScrolled ? 'Scroll the declaration to enable' : ''}>
                    <input aria-label="declaration Accepted"
                      type="radio"
                      name="declarationAccepted"
                      checked={formData.declarationAccepted}
                      onChange={() => setFormData((p) => ({ ...p, declarationAccepted: true }))}
                      disabled={!declFullyScrolled}
                    />
                    <span className="checkmark" />
                    I have read and accept the declaration above.
                  </label>
                </div>
                {errors.declarationAccepted && (
                  <p className="app-error-message">{errors.declarationAccepted}</p>
                )}
              </div>

              <div className="app-form-group">
                <label>Signature</label>
                <div className="signature-wrapper">
                  <ReactSignatureCanvas
                    ref={signatureRef}
                    penColor="blue"
                    canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                    onEnd={handleEndSignature}
                  />
                  <button type="button" className="clear-signature-btn" onClick={clearSignature}>
                    Clear
                  </button>
                </div>
                {!formData.signatureData && (
                  <p className="hint-text">Add your signature to enable the Submit button.</p>
                )}
                {errors.signatureData && <p className="app-error-message">{errors.signatureData}</p>}
              </div>
            </>
          )}
        </div>

        <div className="step-nav-buttons">
          {step > 1 && (
            <button type="button" onClick={handlePrevious} className="prev-btn">
              Previous
            </button>
          )}
          <button
            type="button"
            onClick={persistCurrentStepAsDraft}
            className="prev-btn"
            disabled={isAdvancing || docUploading || submitting}
          >
            {isAdvancing ? 'Saving…' : 'Save Draft'}
          </button>
          {step < 5 && (
            <button
              type="button"
              onClick={handleNext}
              className="next-btn"
              disabled={!canGoNext}
            >
              {isAdvancing ? 'Saving…' : 'Next'}
            </button>
          )}
          {step === 5 && (
            <button
              type="submit"
              className="app-submit-btn"
              disabled={submitting || !formData.signatureData}
              title={!formData.signatureData ? 'Sign above to enable submit' : ''}
            >
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
          )}
        </div>
      </form>

      {showCoResearcherPopup && (
        <ModalLayer className="co-modal-overlay" onClick={() => setShowCoResearcherPopup(false)}>
          <div className="co-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Co-Researcher</h3>
            <div className="popup-form-group">
              <label>Full Name</label>
              <input aria-label="Full Name"
                type="text"
                value={newCoResearcher.fullName}
                onChange={(e) => setNewCoResearcher((p) => ({ ...p, fullName: e.target.value }))}
                placeholder="Full Name"
              />
            </div>
            <div className="popup-form-group">
              <label>Designation</label>
              <input aria-label="Occupation"
                type="text"
                value={newCoResearcher.occupation}
                onChange={(e) => setNewCoResearcher((p) => ({ ...p, occupation: e.target.value }))}
                placeholder="Occupation"
              />
            </div>
            <div className="modal-buttons">
              <button type="button" className="generate-btn" onClick={handleAddCoResearcher}>
                Add
              </button>
              <button
                type="button"
                className="clear-signature-btn"
                onClick={() => setShowCoResearcherPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      {showConsentPopup && (
        <ModalLayer className="consent-modal-overlay animated-consent-modal">
          <div className="consent-modal">
            <h3>Generate Consent Form</h3>
            <div className="popup-form-group">
              <label>Institution</label>
              <input aria-label="Institution"
                type="text"
                className="animated-institution-field"
                value={institutionInput}
                onChange={(e) => setInstitutionInput(e.target.value)}
                placeholder="Institution"
              />
            </div>
            <div className="popup-form-group">
              <label>Principal Investigator</label>
              <input aria-label="Principal Investigator"
                type="text"
                className="animated-principal-field"
                value={principalInvestigatorInput}
                onChange={(e) => setPrincipalInvestigatorInput(e.target.value)}
                placeholder="Principal Investigator"
              />
            </div>
            <div className="popup-form-group">
              <label>Purpose of Research</label>
              <input aria-label="Purpose of Research"
                type="text"
                className="animated-purpose-field"
                value={purposeOfResearchInput}
                onChange={(e) => setPurposeOfResearchInput(e.target.value)}
                placeholder="Purpose of Research"
              />
            </div>
            <div className="popup-form-group">
              <label>What Participants Will Do</label>
              <textarea aria-label="Describe what participants will do..."
                className="animated-participant-field"
                value={participantActivitiesInput}
                onChange={(e) => setParticipantActivitiesInput(e.target.value)}
                placeholder="Describe what participants will do..."
                rows={4}
              />
            </div>
            <div className="modal-buttons">
              <button type="button" className="generate-btn" onClick={handleConsentGenerate}>
                Generate
              </button>
              <button
                type="button"
                className="clear-signature-btn"
                onClick={() => setShowConsentPopup(false)}
              >
                Close
              </button>
            </div>
            <p>Consent Form has been generated and downloaded. Please sign and attach manually.</p>
          </div>
        </ModalLayer>
      )}

      {submissionPopupVisible && (
        <ModalLayer
          className="success-popup-overlay"
          onClick={() => { setSubmissionPopupVisible(false); navigate('/dashboard'); }}
        >
          <div className="success-popup-content" onClick={(e) => e.stopPropagation()}>
            <p>🎉 Application submitted successfully!</p>
            <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
              <ul style={{ marginLeft: '1.1rem' }}>
                <li>You’ll receive a confirmation email shortly.</li>
                <li>Track progress on your Dashboard under <strong>Recent Applications</strong>.</li>
                <li>If requested, you can upload additional documents during review.</li>
              </ul>
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                className="generate-btn"
                onClick={() => navigate('/applications')}
              >
                View My Applications
              </button>
              <button
                type="button"
                className="generate-btn"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </ModalLayer>
      )}
    </div>
  );
};

export default ApplicationForm;
